import {
  NO_STORE,
  type FxRates,
  type FxResponse,
  type Store,
  type TripItem,
  type TripWithItems,
} from '@sp/shared'
import type { TripSync } from '../../lib/tripSync.ts'

export interface TripViewProps {
  trip: TripWithItems
  sync: TripSync
  /** Vsechny obchody skupiny vcetne skrytych — polozka muze ukazovat i na ne. */
  stores: Store[]
  fx: FxResponse | null
}

export function routeOf(trip: TripWithItems, stores: readonly Store[]): Store[] {
  return trip.storeIds.flatMap((id) => stores.find((s) => s.id === id) ?? [])
}

export interface Section {
  key: string
  store: Store | null
  title: string
  items: TripItem[]
}

/** Polozky po obchodech v poradi trasy; co na trase neni, jde na konec. */
export function sectionsFor(trip: TripWithItems, stores: readonly Store[]): Section[] {
  const route = routeOf(trip, stores)
  const items = [...trip.items].sort((a, b) => a.position - b.position)
  const sections: Section[] = route.map((store) => ({
    key: store.id,
    store,
    title: store.name,
    items: items.filter((i) => i.storeId === store.id),
  }))
  const onRoute = new Set(route.map((s) => s.id))
  const other = items.filter((i) => !i.storeId || !onRoute.has(i.storeId))
  if (other.length > 0) {
    sections.push({ key: NO_STORE, store: null, title: route.length > 0 ? 'Ostatní' : 'Seznam', items: other })
  }
  return sections
}

/** Kurz zamrazeny k nakupu ma prednost pred dnesnim. */
export function ratesFor(trip: TripWithItems, fx: FxResponse | null): FxRates {
  return {
    eurCzk: trip.fxEurCzk ?? fx?.eurCzk ?? null,
    plnCzk: trip.fxPlnCzk ?? fx?.plnCzk ?? null,
  }
}

export function storeOfItem(item: TripItem, stores: readonly Store[]): Store | null {
  return item.storeId ? (stores.find((s) => s.id === item.storeId) ?? null) : null
}

export function otherStores(route: readonly Store[], storeId: string | null): Store[] {
  return route.filter((s) => s.id !== storeId)
}

/** Dalsi obchod na trase — kam se nenalezena polozka nejcasteji presouva. */
export function nextStore(route: readonly Store[], storeId: string | null): Store | undefined {
  const index = route.findIndex((s) => s.id === storeId)
  return index < 0 ? undefined : route[index + 1]
}

export function toggled(set: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(set)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}
